import { useState, useMemo, FC, useEffect } from 'react';
import { getOrderAudit } from '../../../fetch';
import { formatShortFriendlyDateWithTime, formatPascalStringToSpaces } from '../../../helpers';
import { IOrderAudit, IOrderDetail } from '../../../models';
import { Grid, Typography, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { MobileOrderHistory } from './mobile-order-history';
import { Table, ImageUpload } from '../../../components';

interface IOrderHistory {
  currentOrder: IOrderDetail;
}
export const formatHistoryChangeValue = (field: string, value: string) => {
  if (field === 'FileContents' && value) {
    return <ImageUpload disabled={true} handleFileChange={null} image={value} showLabel={false} showSource={false} />;
  }
  return value;
};
export const formatHistoryChangeField = (field: string) => {
  if (field === 'VcFieldOrder') {
    return 'Notes';
  } else if (field) {
    return formatPascalStringToSpaces(field);
  } else {
    return field;
  }
};

export const OrderHistory: FC<IOrderHistory> = ({ currentOrder }) => {
  const classes = useStyles();
  const [currentOrderHistory, setCurrentOrderHistory] = useState<IOrderAudit[]>([]);
  const [isLoadingOrderHistory, setLoadingOrderHistory] = useState<boolean>(true);
  const fetchOrderHistory = async () => {
    try {
      const res = await getOrderAudit(currentOrder.fieldOrderId);
      setCurrentOrderHistory(res.records);
    } catch (error) {
      console.error(error);
    }
    setLoadingOrderHistory(false);
  };
  useEffect(() => {
    if (currentOrder) {
      fetchOrderHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrder]);
  const isMobile = useMediaQuery('(max-width: 960px)');
  const historyColumns = useMemo(
    () => [
      {
        Header: 'Date/time',
        accessor: 'createdDate',
        sort: false,
        Cell: ({ cell: { row } }) => {
          return <>{formatShortFriendlyDateWithTime(row.values.createdDate)}</>;
        }
      },
      {
        Header: 'Person',
        accessor: 'createdBy',
        sort: false,
        Cell: ({ cell: { row } }) => {
          return <>{row.values.createdBy}</>;
        }
      },
      {
        Header: 'Device/IP',
        accessor: 'ipAddress',
        sort: false,
        Cell: ({ cell: { row } }) => {
          return <>{row.values.ipAddress || null}</>;
        }
      },
      {
        Header: 'Change',
        accessor: 'propertyName',
        sort: false,
        Cell: ({ cell: { row } }) => {
          return <>{formatHistoryChangeField(row.values.propertyName) || null}</>;
        }
      },
      {
        Header: 'Before Change',
        accessor: 'originalValue',
        sort: false,
        Cell: ({ cell: { row } }) => {
          return <>{formatHistoryChangeValue(row.values.propertyName, row.values.originalValue) || null}</>;
        }
      },
      {
        Header: 'After Change',
        accessor: 'currentValue',
        sort: false,
        Cell: ({ cell: { row } }) => {
          return <>{formatHistoryChangeValue(row.values.propertyName, row.values.currentValue) || null}</>;
        }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentOrderHistory]
  );
  return (
    <>
      <Grid item>
        <Typography variant="h6" className={classes.partsTitle}>
          History
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Table
          columns={historyColumns}
          isLoading={isLoadingOrderHistory}
          data={
            currentOrderHistory
              ? currentOrderHistory.sort(function (a, b) {
                  // sort the values by date with the most recent at the top of the table
                  return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
                })
              : []
          }
          hidePagination
          ResponsiveComponent={isMobile ? MobileOrderHistory : undefined}
          noResultsText="No Order History Found"
        />
      </Grid>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  partsTitle: {
    marginTop: theme.spacing(1)
  }
}));
